import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import { IMAGES } from "@/data/content";

const OFFERS = [
  {
    id: "build",
    name: "BUILD Coaching",
    label: "Hybrid coaching",
    price: "$100",
    featured: true,
    short:
      "For when you want more than a program. You want someone beside you as you learn, adjust and build confidence.",
    long: [
      "BUILD Coaching is for women who want individualised training with consistent, hands-on support.",
      "We work together week to week across your training, technique, food and the psychology underneath it all. You won't be left wondering whether you're doing something correctly or whether the plan still suits you. We keep looking at what your body, your lifts and your life are telling us, then adjust as you go.",
      "Your fortnightly coaching sessions give us dedicated time to work on your lifts, solve problems and help things click. Between sessions, you still have ongoing feedback and support, so your coaching doesn't stop when the session ends.",
    ],
    included: [
      "A personalised strength program built around your goals, experience, schedule and current capacity",
      "Fortnightly one-to-one coaching sessions, completed in person or online",
      "Weekly check-ins through The Strength Movement coaching app",
      "Video feedback on your lifts and clear, focused technique guidance",
      "Ongoing program adjustments as you progress",
      "Support with food, fuelling and your relationship with eating, without rigid dieting",
      "Coaching across the psychological side of training, including confidence, fear, frustration and overthinking",
      "Direct messaging support between check-ins",
      "Preparation for strength goals, testing days or powerlifting competitions where relevant",
    ],
    suited: [
      "You want regular contact and a closer coaching relationship",
      "You learn best when someone can watch, explain and help you practise",
      "You want support with both the physical and psychological sides of training",
      "You're tired of constantly questioning your technique or changing your plan",
      "You want to understand what works for you, not simply follow instructions",
    ],
    closing:
      "You won't just receive a plan. You'll learn how to train with more clarity, respond to your body and build strength you can trust.",
    button: "Start BUILD Coaching",
    interest: "Build Coaching",
  },
  {
    id: "online",
    name: "BUILD Online",
    label: "Online coaching",
    price: "$85",
    featured: false,
    short:
      "The complete BUILD coaching approach, delivered online for women who don't need regular in-person sessions.",
    long: [
      "BUILD Online gives you an individualised strength program and ongoing coaching without needing to train in the same gym as me.",
      "This is not a downloadable template or a program that gets sent to you and forgotten. I still coach the person completing it.",
      "We review your training, look at your lifting videos, adjust the program and work through the questions that arise around technique, progress, food and confidence. You receive clear guidance without having to sort through more conflicting information on your own.",
    ],
    included: [
      "A personalised strength program built around your goals, experience, equipment and schedule",
      "Weekly check-ins through The Strength Movement coaching app",
      "Detailed video feedback on your lifts",
      "Ongoing programming adjustments based on your progress and feedback",
      "Guidance around food, fuelling and eating without rigid dieting",
      "Support with confidence, overthinking and the psychological side of training",
      "Direct messaging support between check-ins",
      "Preparation for strength goals, testing days or powerlifting competitions where relevant",
    ],
    suited: [
      "You train independently but want expert eyes on your technique and programming",
      "You don't live near Kim or don't need regular in-person sessions",
      "You're comfortable filming your lifts and communicating online",
      "You want personalised coaching rather than a generic program",
      "You want someone to help you interpret what is happening and decide what to do next",
    ],
    closing:
      "You'll still be doing the training independently, but you won't be left to figure everything out alone.",
    button: "Start Online Coaching",
    interest: "BUILD Online",
  },
];

const OfferCard = ({ offer, index }) => {
  const dark = offer.featured;
  return (
    <Reveal delay={index * 0.08}>
      <article
        id={offer.id}
        data-testid={`offer-card-${offer.id}`}
        className={`relative grain scroll-mt-28 ${
          dark
            ? "bg-ink text-white shadow-2xl shadow-ink/20"
            : "bg-white text-ink border border-ink/10"
        }`}
      >
        {dark && (
          <span
            data-testid="offer-build-badge"
            className="absolute right-6 top-6 rounded-full bg-ocean px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white"
          >
            Most support
          </span>
        )}
        <div className="relative z-10 p-8 md:p-14">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className={`font-display text-6xl italic font-light ${dark ? "text-ocean" : "text-ocean/40"}`}>
                {String(index + 1).padStart(2, "0")}
              </span>
              <p className={`label-text mt-4 ${dark ? "text-ocean" : "text-ocean"}`}>{offer.label}</p>
              <h3 className="mt-3 font-display text-4xl md:text-5xl leading-tight">{offer.name}</h3>
              <p className="mt-6 flex items-baseline gap-2" data-testid={`offer-price-${offer.id}`}>
                <span className="font-display text-6xl md:text-7xl">{offer.price}</span>
                <span className={`text-lg italic font-display ${dark ? "text-white/60" : "text-ink/50"}`}>
                  per week
                </span>
              </p>
              <p className={`mt-6 font-display text-xl md:text-2xl italic font-light leading-snug ${dark ? "text-white/80" : "text-ink/70"}`}>
                {offer.short}
              </p>
              <Link
                to={`/contact?interest=${encodeURIComponent(offer.interest)}`}
                data-testid={`offer-start-${offer.id}-button`}
                className={`mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-colors duration-300 ${
                  dark
                    ? "bg-white text-ink hover:bg-ocean hover:text-white"
                    : "bg-ink text-white hover:bg-ocean"
                }`}
              >
                {offer.button}
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className={`space-y-4 text-sm md:text-base leading-relaxed ${dark ? "text-white/75" : "text-ink/70"}`}>
                {offer.long.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <div className="mt-10 grid gap-10 md:grid-cols-2">
                <div data-testid={`offer-included-${offer.id}`}>
                  <h4 className={`label-text ${dark ? "text-ocean" : "text-ocean"}`}>What's included</h4>
                  <ul className="mt-5 space-y-3.5">
                    {offer.included.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check size={16} strokeWidth={2} className="mt-1 shrink-0 text-ocean" />
                        <span className={`text-sm leading-relaxed ${dark ? "text-white/80" : "text-ink/75"}`}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div data-testid={`offer-suited-${offer.id}`}>
                  <h4 className={`label-text ${dark ? "text-ocean" : "text-ocean"}`}>Best suited to you if</h4>
                  <ul className={`mt-5 space-y-3.5 divide-y ${dark ? "divide-white/10" : "divide-ink/10"}`}>
                    {offer.suited.map((item, i) => (
                      <li key={i} className={`pt-3.5 first:pt-0 text-sm leading-relaxed ${dark ? "text-white/80" : "text-ink/75"}`}>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className={`mt-10 border-l-2 border-ocean pl-5 font-display text-lg md:text-xl italic font-light leading-snug ${dark ? "text-white/85" : "text-ink/80"}`}>
                {offer.closing}
              </p>
            </div>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

const Offers = () => {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  return (
    <main data-testid="offers-page">
      {/* INTRO */}
      <section className="bg-mist grain">
        <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <p className="label-text text-ocean" data-testid="offers-eyebrow">Coaching with Kim</p>
          <h1
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight text-balance"
            data-testid="offers-heading"
          >
            <MaskedLines lines={["You don't need another", "plan to second-guess."]} />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-10 space-y-5 text-base md:text-lg leading-relaxed text-ink/70"
            data-testid="offers-intro"
          >
            <p>
              You may already know how to train. You might have followed programs,
              watched technique videos, tracked your food or tried to piece
              together advice from different people.
            </p>
            <p>But knowing more hasn't necessarily made things feel clearer.</p>
            <p>
              You may still wonder whether your technique is right, whether you're
              pushing hard enough, why a lift has stopped progressing or whether
              the way you're eating is actually supporting you.
            </p>
            <p>
              That is where coaching becomes different from simply being given a
              program.
            </p>
            <p>
              I build the training around you, coach you through how to execute it
              and help you make sense of what is happening in your lifts, your
              body and your relationship with food.
            </p>
            <p className="font-display text-xl md:text-2xl italic font-light text-ink">
              The aim isn't to make you dependent on coaching. It's to help you
              become stronger, more capable and more confident in the decisions
              you make.
            </p>
            <div className="pt-6 max-w-md" data-testid="coaching-testimonial-video">
              <div className="overflow-hidden border border-ink/10 bg-white shadow-lg shadow-ink/5">
                <video
                  src="/videos/tsm-testimonial-web.mp4"
                  poster="/videos/tsm-testimonial-poster.jpg"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  className="w-full"
                />
              </div>
              <p className="mt-3 text-xs uppercase tracking-[0.25em] text-ink/40">
                Hear it from a client
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* OFFERS */}
      <section className="bg-white" data-testid="offers-list-section">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 py-24 md:py-32">
          <Reveal>
            <p className="label-text text-ocean">Your options</p>
            <h2 className="mt-6 max-w-2xl font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-balance">
              Choose the level of support
              <span className="italic font-light text-ink/50"> that fits you</span>
            </h2>
          </Reveal>
          <div className="mt-14 space-y-10">
            {OFFERS.map((offer, i) => (
              <OfferCard key={offer.id} offer={offer} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FOOD FREEDOM STRIP */}
      <section id="food-freedom" className="bg-white scroll-mt-28" data-testid="offers-food-freedom-strip">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 pb-24 md:pb-32">
          <Reveal>
            <div className="grid items-center gap-10 border border-ink/10 bg-mist p-8 md:p-14 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <p className="label-text text-ocean">Also part of coaching</p>
                <h2 className="mt-4 font-display text-3xl md:text-4xl">
                  Food Freedom <span className="italic font-light text-ink/50">Coaching</span>
                </h2>
                <p className="mt-5 max-w-xl text-sm md:text-base leading-relaxed text-ink/70">
                  Food isn't the problem. This work is about healing,
                  understanding how we got here, and building the tools to move
                  forward, across both the science and the psychology of your
                  relationship with food.
                </p>
              </div>
              <div className="lg:col-span-5 lg:pl-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <ul className="space-y-2.5 text-sm text-ink/70">
                    {["The physiology of hunger", "Female hormones & metabolism", "Fuelling performance"].map((i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="h-1 w-1 rounded-full bg-ocean" />{i}
                      </li>
                    ))}
                  </ul>
                  <ul className="space-y-2.5 text-sm text-ink/70">
                    {["Food rules & fear foods", "Body image & identity", "Self-trust"].map((i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="h-1 w-1 rounded-full bg-ocean" />{i}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to="/contact?interest=Food%20Freedom%20Coaching"
                  data-testid="food-freedom-enquire-button"
                  className="group mt-7 inline-flex items-center gap-2 text-sm font-bold text-ink transition-colors hover:text-ocean"
                >
                  Enquire about Food Freedom
                  <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-mist" data-testid="offers-comparison-section">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 py-24 md:py-32">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl leading-[0.98] tracking-tight text-balance">
              Both options give you
              <span className="italic font-light text-ink/50"> more than a program</span>
            </h2>
            <div className="mt-8 space-y-5 text-base md:text-lg leading-relaxed text-ink/70">
              <p>
                Whichever option you choose, your coaching will be built around the
                same principle: you are a person to understand, not a problem to
                fix.
              </p>
              <p>
                We'll work with your training history, your goals, your body, your
                schedule and your relationship with food. You'll know why you are
                doing what you are doing, receive feedback as things change and
                have somewhere to bring the questions that usually lead to
                overthinking.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mx-auto mt-12 max-w-2xl overflow-hidden" data-testid="comparison-coach-image">
              <img
                src={IMAGES.coaching}
                alt="Coach Kim coaching Lizi through a lift"
                loading="lazy"
                className="w-full"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 border-y border-ink/10 divide-y divide-ink/10">
              <div className="flex flex-wrap items-baseline justify-between gap-3 py-7" data-testid="comparison-build">
                <div>
                  <p className="font-display text-2xl">BUILD Coaching</p>
                  <p className="mt-1 text-sm text-ink/60">
                    Fortnightly one-to-one coaching sessions, in person or online.
                  </p>
                </div>
                <p className="font-display text-3xl text-ocean">
                  $100<span className="text-base italic text-ink/50"> p/wk</span>
                </p>
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-3 py-7" data-testid="comparison-online">
                <div>
                  <p className="font-display text-2xl">BUILD Online</p>
                  <p className="mt-1 text-sm text-ink/60">
                    Delivered remotely through programming, check-ins, video
                    feedback and messaging support.
                  </p>
                </div>
                <p className="font-display text-3xl text-ocean">
                  $85<span className="text-base italic text-ink/50"> p/wk</span>
                </p>
              </div>
            </div>
            <p className="mt-6 text-sm text-ink/50">
              The main difference is the level of live coaching.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-ink text-white grain" data-testid="offers-final-cta">
        <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-10 py-24 md:py-36">
          <Reveal>
            <p className="label-text text-ocean">The next step</p>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-balance">
              If you're still wondering
              <span className="italic font-light text-white/60"> whether you're ready</span>
            </h2>
            <div className="mt-8 space-y-5 text-base md:text-lg leading-relaxed text-white/70">
              <p>
                You do not need to be stronger, leaner, more experienced or more
                confident before you begin.
              </p>
              <p>You also don't need to arrive with a perfectly defined goal.</p>
              <p>
                You might simply know that you're tired of going in circles. You
                want to feel clearer in the gym, stronger under the bar and less
                consumed by decisions around training and food.
              </p>
              <p className="font-display text-xl md:text-2xl italic font-light text-white">
                That is enough of a place to start.
              </p>
              <p>
                If we've already spoken or completed a first session together, you
                can choose the option that feels right and take the next step. If
                you're still unsure, send an enquiry and tell me what you're
                currently navigating. We can work out whether coaching, and which
                type of coaching, fits you.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact?interest=Build%20Coaching"
                data-testid="offers-start-coaching-button"
                className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-ink transition-colors duration-300 hover:bg-ocean hover:text-white"
              >
                Start coaching
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/contact?interest=Something%20else"
                data-testid="offers-ask-kim-button"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:border-ocean hover:text-ocean"
              >
                Ask Kim a question
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Offers;
